/**
 * AdminSection - Admin-only development and testing controls
 * Only visible to users with admin emails
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from '@/components/ui/glass-card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from '@/hooks/useToast'
import { useUserLimits } from '@/hooks/useUserLimits'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/contexts/AuthContext'
import { AlertTriangle, Crown, Radio, RotateCcw, Trash2 } from 'lucide-react'

const TIERS = [
  { value: 'pro', label: 'Pro', description: '1,000 trials/day, 1 parallel' },
  { value: 'max', label: 'Max', description: '100,000 trials/day, 10 parallel' },
]

export function AdminSection() {
  const { toast } = useToast()
  const { data: limits, refetch: refetchLimits } = useUserLimits()
  const api = useApi()
  const { userInfo } = useAuth()

  // All hooks MUST be called before any conditional returns (React rules of hooks)
  const [adminLoading, setAdminLoading] = useState(false)
  const [tierLoading, setTierLoading] = useState(false)
  const [selectedTier, setSelectedTier] = useState<string | undefined>(undefined)
  const [sseDebugEnabled, setSseDebugEnabled] = useState(
    () => localStorage.getItem('sseDebugPanelEnabled') === 'true'
  )

  const isAdmin = userInfo?.isAdmin === true
  const currentTier = limits?.tier || 'pro'

  // Early return AFTER all hooks are called
  if (!isAdmin) {
    return null
  }

  const handleResetLimits = async () => {
    setAdminLoading(true)
    try {
      await api.post('/admin/reset-limits')
      toast({
        title: 'Success',
        description: 'Your usage limits have been reset to 0.',
      })
      await refetchLimits()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to reset limits.',
        variant: 'destructive',
      })
    } finally {
      setAdminLoading(false)
    }
  }

  const handleDeleteMe = async () => {
    if (!confirm('Are you sure you want to delete your user limits record? This is destructive.')) return

    setAdminLoading(true)
    try {
      await api.delete('/admin/me')
      toast({
        title: 'Success',
        description: 'Your user record has been deleted.',
      })
      setTimeout(() => window.location.reload(), 1000)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete user record.',
        variant: 'destructive',
      })
    } finally {
      setAdminLoading(false)
    }
  }

  const handleSetTier = async (tier: string) => {
    setSelectedTier(tier)
    setTierLoading(true)
    try {
      await api.post('/admin/set-tier', { tier })
      toast({
        title: 'Success',
        description: `Tier changed to "${tier}" successfully.`,
      })
      await refetchLimits()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to change tier.',
        variant: 'destructive',
      })
      setSelectedTier(undefined)
    } finally {
      setTierLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Warning banner */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
        <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-medium text-amber-200">Development Tools</h4>
          <p className="text-sm text-muted-foreground mt-1">
            These controls are for testing subscription tiers and debugging.
            Changes here affect your actual account data.
          </p>
        </div>
      </div>

      {/* Admin controls grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Tier Switcher */}
        <GlassCard>
          <GlassCardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-amber-400" />
              <GlassCardTitle className="text-base">Subscription Tier</GlassCardTitle>
            </div>
            <GlassCardDescription className="text-xs">
              Current: <span className="text-amber-400 font-medium">{currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}</span>
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <Select
              value={selectedTier || currentTier}
              onValueChange={handleSetTier}
              disabled={tierLoading}
            >
              <SelectTrigger className="w-full bg-zinc-900 border-white/10 text-zinc-200 hover:bg-zinc-800">
                <SelectValue placeholder="Select tier" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10">
                {TIERS.map((tier) => (
                  <SelectItem
                    key={tier.value}
                    value={tier.value}
                    className="text-zinc-200 focus:bg-white/10 focus:text-white py-2.5 pl-3"
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{tier.label}</span>
                      <span className="text-xs text-zinc-400">{tier.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tierLoading && (
              <p className="text-xs text-muted-foreground animate-pulse mt-2">Updating...</p>
            )}
          </GlassCardContent>
        </GlassCard>

        {/* Reset Usage */}
        <GlassCard>
          <GlassCardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-blue-400" />
              <GlassCardTitle className="text-base">Reset Usage</GlassCardTitle>
            </div>
            <GlassCardDescription className="text-xs">
              Reset daily trial count to 0
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <Button
              onClick={handleResetLimits}
              disabled={adminLoading}
              variant="outline"
              size="sm"
              className="w-full"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Usage
            </Button>
          </GlassCardContent>
        </GlassCard>

        {/* Delete Record */}
        <GlassCard>
          <GlassCardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Trash2 className="h-4 w-4 text-red-400" />
              <GlassCardTitle className="text-base">Delete Data</GlassCardTitle>
            </div>
            <GlassCardDescription className="text-xs">
              Remove your limits record
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <Button
              onClick={handleDeleteMe}
              disabled={adminLoading}
              variant="destructive"
              size="sm"
              className="w-full"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Record
            </Button>
          </GlassCardContent>
        </GlassCard>

        {/* SSE Debug Panel Toggle */}
        <GlassCard>
          <GlassCardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-green-400" />
              <GlassCardTitle className="text-base">SSE Debug</GlassCardTitle>
            </div>
            <GlassCardDescription className="text-xs">
              Show floating SSE record/playback panel
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sseDebugEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked
                  setSseDebugEnabled(enabled)
                  localStorage.setItem('sseDebugPanelEnabled', String(enabled))
                  // Dispatch storage event so the panel reacts immediately in the same tab
                  window.dispatchEvent(
                    new StorageEvent('storage', {
                      key: 'sseDebugPanelEnabled',
                      newValue: String(enabled),
                    })
                  )
                }}
                className="rounded border-dark-600 bg-dark-800 text-brand-blue focus:ring-brand-blue"
              />
              <span className="text-sm">Enable Panel</span>
            </label>
          </GlassCardContent>
        </GlassCard>
      </div>
    </div>
  )
}

export function useIsAdmin(): boolean {
  const { userInfo } = useAuth()
  return userInfo?.isAdmin === true
}
